// ConvoCX unified inbox — data hook (CON-845).
// Owns conversations, filters, selection, messages, and mutations, and wires
// the WebSocket stream into live updates + notifications.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { useInboxSocket } from './socket';
import type { Conversation, ConversationStatus, Label, Message, TeamMember } from './types';

export type ChannelFilter = string | 'all';
export type StatusFilter = ConversationStatus | 'all' | 'unassigned' | 'assigned-me';

export interface InboxFilters {
  search: string;
  /** Selected channel ids; empty array = all channels. */
  channels: string[];
  /** Selected statuses (incl. UI sentinels unassigned/assigned-me); empty array = all. */
  statuses: string[];
}

const EMPTY_FILTERS: InboxFilters = { search: '', channels: [], statuses: [] };

export function useInbox(token: string | null) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [filters, setFilters] = useState<InboxFilters>(EMPTY_FILTERS);
  const [labels, setLabels] = useState<Label[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Message[]>([]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchConversations = useCallback(async (overrides?: Partial<InboxFilters>) => {
    const f = { ...filtersRef.current, ...(overrides ?? {}) };
    setLoading(true);
    setError(null);
    try {
      const res = await api.listConversations({
        search: f.search || undefined,
        channel_id: f.channels.length ? f.channels : undefined,
        status: f.statuses.length ? f.statuses : undefined,
        // "assigned-me" is a UI sentinel resolved server-side to the current user.
        assigned_to: f.statuses.includes('assigned-me') && currentUserId ? currentUserId : undefined,
        // Always route to the local unified queue (not the portal path): the
        // P1-T1 API returns the portal's conversation list when no filter
        // params are present, which hides widget/email/WhatsApp conversations
        // stored in the local DB. Sending archived=false forces the local path.
        archived: false,
      });
      setConversations(res.data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // initial + refetch when filters change
  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.channels, filters.statuses, currentUserId]);

  const loadMeta = useCallback(async () => {
    try {
      const [lbl, tm] = await Promise.all([api.listLabels(), api.listTeamMembers()]);
      setLabels(lbl.labels);
      setTeam(tm.members);
    } catch {
      // meta load is non-fatal
    }
  }, []);

  useEffect(() => {
    if (token) loadMeta();
  }, [token, loadMeta]);

  const fetchMessages = useCallback(async (conversationId: string) => {
    setMessagesLoading(true);
    try {
      const res = await api.listMessages(conversationId);
      setMessages(res.data);
      api.markRead(conversationId).then((updated) => {
        setConversations((prev) => prev.map((c) => (c.id === conversationId ? { ...c, unread_count: 0, last_read_at: updated.last_read_at ?? c.last_read_at } : c)));
      }).catch(() => {});
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const selectConversation = useCallback((id: string | null) => {
    setSelectedId(id);
    if (id) {
      fetchMessages(id);
    } else {
      setMessages([]);
    }
  }, [fetchMessages]);

  const sendMessage = useCallback(async (body: string) => {
    if (!selectedId) return null;
    try {
      const msg = await api.sendMessage(selectedId, body);
      setMessages((prev) => [...prev, msg]);
      const preview = body.slice(0, 120);
      setConversations((prev) =>
        prev.map((c) => (c.id === selectedId ? { ...c, last_message_preview: preview, last_message_at: msg.created_at, last_message_direction: 'outbound' } : c)),
      );
      return msg;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to send message');
      return null;
    }
  }, [selectedId]);

  const updateConversation = useCallback(async (id: string, patch: Parameters<typeof api.updateConversation>[1]) => {
    try {
      const updated = await api.updateConversation(id, patch);
      setConversations((prev) => prev.map((c) => (c.id === id ? updated : c)));
      return updated;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update conversation');
      return null;
    }
  }, []);

  const toggleStar = useCallback(async (id: string) => {
    try {
      const updated = await api.toggleStar(id);
      setConversations((prev) => prev.map((c) => (c.id === id ? updated : c)));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update star');
    }
  }, []);

  const toggleArchive = useCallback(async (id: string) => {
    try {
      const updated = await api.toggleArchive(id);
      setConversations((prev) => prev.map((c) => (c.id === id ? updated : c)));
      if (selectedId === id) setSelectedId(null);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to archive conversation');
    }
  }, [selectedId]);

  const assignLabel = useCallback(async (conversationId: string, label: Label) => {
    try {
      await api.assignLabel(conversationId, label.id);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, labels: [...(c.labels ?? []).filter((l) => l.id !== label.id), label] }
            : c,
        ),
      );
    } catch (err: any) {
      setError(err?.message ?? 'Failed to assign label');
    }
  }, []);

  const removeLabel = useCallback(async (conversationId: string, labelId: string) => {
    try {
      await api.removeLabel(labelId, conversationId);
      setConversations((prev) =>
        prev.map((c) => (c.id === conversationId ? { ...c, labels: (c.labels ?? []).filter((l) => l.id !== labelId) } : c)),
      );
    } catch (err: any) {
      setError(err?.message ?? 'Failed to remove label');
    }
  }, []);

  // Real-time: new messages + conversation updates
  useInboxSocket({
    token,
    enabled: !!token,
    onMessage: (msg) => {
      if (msg.conversation_id === selectedId) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      } else {
        setNotifications((prev) => (prev.some((n) => n.id === msg.id) ? prev : [...prev, msg]));
        setConversations((prev) => prev.map((c) => (c.id === msg.conversation_id ? { ...c, unread_count: (c.unread_count ?? 0) + 1 } : c)));
      }
    },
    onConversationUpdate: (conv) => {
      setConversations((prev) => prev.map((c) => (c.id === conv.id ? { ...c, ...conv } : c)));
    },
    onAuthenticated: setCurrentUserId,
  });

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId],
  );

  return {
    conversations,
    loading,
    error,
    labels,
    team,
    selectedId,
    selectedConversation,
    messages,
    messagesLoading,
    filters,
    setFilters,
    selectConversation,
    sendMessage,
    updateConversation,
    toggleStar,
    toggleArchive,
    assignLabel,
    removeLabel,
    fetchConversations,
    notifications,
    setNotifications,
    clearNotifications: () => setNotifications([]),
  };
}